import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';

export class WarehousePDFGenerator {
  static async generateInventoryReport(data) {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Header
    const qrCodeDataUrl = await QRCode.toDataURL(`INV-${data.reportId}`);
    doc.addImage(qrCodeDataUrl, 'PNG', 245, 5, 35, 35);

    doc.setFontSize(20);
    doc.setTextColor(44, 62, 80);
    doc.text('Warehouse Inventory Report', 10, 15);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Report ID: ${data.reportId}`, 10, 22);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 10, 27);
    doc.text(`Warehouse: ${data.warehouse}`, 10, 32);

    let yPos = 45;

    // Summary Section
    doc.setFillColor(52, 144, 220);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.rect(10, yPos, 277, 8, 'F');
    doc.text('Summary', 12, yPos + 5.5);
    doc.setTextColor(0, 0, 0);

    yPos += 15;
    doc.autoTable({
      startY: yPos,
      theme: 'plain',
      body: [
        ['Total Items', data.summary.totalItems],
        ['Total Value', `SAR ${data.summary.totalValue.toLocaleString()}`],
        ['Categories', data.summary.categories],
        ['Low Stock Items', data.summary.lowStock],
      ],
      margin: { left: 10 },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: 'bold' },
        1: { cellWidth: 60 },
      },
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Inventory Items Table
    doc.setFillColor(52, 144, 220);
    doc.setTextColor(255, 255, 255);
    doc.rect(10, yPos, 277, 8, 'F');
    doc.text('Inventory Items', 12, yPos + 5.5);
    doc.setTextColor(0, 0, 0);

    yPos += 15;
    doc.autoTable({
      startY: yPos,
      head: [['SKU', 'Item Name', 'Category', 'Quantity', 'Location', 'Value (SAR)']],
      body: data.items.map(item => [
        item.sku,
        item.name,
        item.category,
        item.quantity,
        item.location,
        item.value.toLocaleString()
      ]),
      theme: 'striped',
      headStyles: { fillColor: [241, 196, 15], textColor: [0, 0, 0] },
      margin: { left: 10 },
      styles: { fontSize: 8 },
    });

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
    }

    return doc.output('blob');
  }

  static async generateOperationsReport(data) {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const qrCodeDataUrl = await QRCode.toDataURL(`OPS-${data.reportId}`);
    doc.addImage(qrCodeDataUrl, 'PNG', 245, 5, 35, 35);

    doc.setFontSize(20);
    doc.setTextColor(44, 62, 80);
    doc.text('Warehouse Operations Report', 10, 15);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Report ID: ${data.reportId}`, 10, 22);
    doc.text(`Period: ${data.period}`, 10, 27);
    doc.text(`Warehouse: ${data.warehouse}`, 10, 32);

    let yPos = 45;

    // Receiving Section
    doc.setFillColor(52, 144, 220);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.rect(10, yPos, 277, 8, 'F');
    doc.text('Receiving Operations', 12, yPos + 5.5);
    doc.setTextColor(0, 0, 0);

    yPos += 15;
    doc.autoTable({
      startY: yPos,
      head: [['PO Number', 'Supplier', 'Items', 'Status', 'Date']],
      body: data.receiving.map(rec => [
        rec.po,
        rec.supplier,
        rec.items,
        rec.status,
        rec.date
      ]),
      theme: 'striped',
      headStyles: { fillColor: [52, 144, 220], textColor: 255 },
      margin: { left: 10 },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Shipping Section
    doc.setFillColor(52, 144, 220);
    doc.setTextColor(255, 255, 255);
    doc.rect(10, yPos, 277, 8, 'F');
    doc.text('Shipping Operations', 12, yPos + 5.5);
    doc.setTextColor(0, 0, 0);

    yPos += 15;
    doc.autoTable({
      startY: yPos,
      head: [['Order Number', 'Customer', 'Items', 'Status', 'Due Date']],
      body: data.shipping.map(ship => [
        ship.order,
        ship.customer,
        ship.items,
        ship.status,
        ship.dueDate
      ]),
      theme: 'striped',
      headStyles: { fillColor: [52, 144, 220], textColor: 255 },
      margin: { left: 10 },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
    }

    return doc.output('blob');
  }

  static async generateLocationReport(data) {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const qrCodeDataUrl = await QRCode.toDataURL(`LOC-${data.reportId}`);
    doc.addImage(qrCodeDataUrl, 'PNG', 160, 5, 35, 35);

    doc.setFontSize(20);
    doc.setTextColor(44, 62, 80);
    doc.text('Warehouse Location Report', 10, 15);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Report ID: ${data.reportId}`, 10, 22);
    doc.text(`Warehouse: ${data.warehouse}`, 10, 27);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 10, 32);

    let yPos = 45;

    // Zone Summary
    doc.setFillColor(52, 144, 220);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.rect(10, yPos, 190, 8, 'F');
    doc.text('Zone Summary', 12, yPos + 5.5);
    doc.setTextColor(0, 0, 0);

    yPos += 15;
    doc.autoTable({
      startY: yPos,
      head: [['Zone', 'Aisles', 'Racks', 'Shelves', 'Bins', 'Utilization']],
      body: data.zones.map(zone => [
        zone.name,
        zone.aisles,
        zone.racks,
        zone.shelves,
        zone.bins,
        `${zone.utilization}%`
      ]),
      theme: 'striped',
      headStyles: { fillColor: [52, 144, 220], textColor: 255 },
      margin: { left: 10 },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Bin Details
    if (data.bins && data.bins.length > 0) {
      doc.setFillColor(52, 144, 220);
      doc.setTextColor(255, 255, 255);
      doc.rect(10, yPos, 190, 8, 'F');
      doc.text('Bin Details', 12, yPos + 5.5);
      doc.setTextColor(0, 0, 0);

      yPos += 15;
      doc.autoTable({
        startY: yPos,
        head: [['Bin ID', 'Location', 'Item', 'Capacity', 'Used', 'Status']],
        body: data.bins.slice(0, 50).map(bin => [
          bin.id,
          bin.location,
          bin.item || 'Empty',
          bin.capacity,
          bin.used,
          bin.status
        ]),
        theme: 'striped',
        headStyles: { fillColor: [52, 144, 220], textColor: 255 },
        margin: { left: 10 },
        styles: { fontSize: 7 },
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
    }

    return doc.output('blob');
  }

  static async generatePerformanceReport(data) {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const qrCodeDataUrl = await QRCode.toDataURL(`PERF-${data.reportId}`);
    doc.addImage(qrCodeDataUrl, 'PNG', 245, 5, 35, 35);

    doc.setFontSize(20);
    doc.setTextColor(44, 62, 80);
    doc.text('Warehouse Performance Report', 10, 15);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Report ID: ${data.reportId}`, 10, 22);
    doc.text(`Period: ${data.period}`, 10, 27);

    let yPos = 45;

    // Key Metrics
    doc.setFillColor(52, 144, 220);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.rect(10, yPos, 277, 8, 'F');
    doc.text('Key Performance Metrics', 12, yPos + 5.5);
    doc.setTextColor(0, 0, 0);

    yPos += 15;
    doc.autoTable({
      startY: yPos,
      theme: 'plain',
      body: [
        ['Throughput', `${data.metrics.throughput.toLocaleString()} items`],
        ['Accuracy Rate', `${data.metrics.accuracy}%`],
        ['Utilization', `${data.metrics.utilization}%`],
        ['Cost per Order', `SAR ${data.metrics.costPerOrder}`],
        ['Cycle Time', `${data.metrics.cycleTime} days`],
      ],
      margin: { left: 10 },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: 'bold' },
        1: { cellWidth: 60 },
      },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Worker Performance
    if (data.workers && data.workers.length > 0) {
      doc.setFillColor(52, 144, 220);
      doc.setTextColor(255, 255, 255);
      doc.rect(10, yPos, 277, 8, 'F');
      doc.text('Worker Performance', 12, yPos + 5.5);
      doc.setTextColor(0, 0, 0);

      yPos += 15;
      doc.autoTable({
        startY: yPos,
        head: [['Worker', 'Role', 'Pick Rate', 'Accuracy', 'Tasks Completed', 'Efficiency']],
        body: data.workers.map(worker => [
          worker.name,
          worker.role,
          `${worker.pickRate}/hr`,
          `${worker.accuracy}%`,
          worker.tasksCompleted,
          `${worker.efficiency}%`
        ]),
        theme: 'striped',
        headStyles: { fillColor: [52, 144, 220], textColor: 255 },
        margin: { left: 10 },
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
    }

    return doc.output('blob');
  }

  static async generateABCAnalysisReport(data) {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const qrCodeDataUrl = await QRCode.toDataURL(`ABC-${data.reportId}`);
    doc.addImage(qrCodeDataUrl, 'PNG', 160, 5, 35, 35);

    doc.setFontSize(20);
    doc.setTextColor(44, 62, 80);
    doc.text('ABC Analysis Report', 10, 15);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Report ID: ${data.reportId}`, 10, 22);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 10, 27);

    let yPos = 45;

    // ABC Classification Summary
    doc.setFillColor(52, 144, 220);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.rect(10, yPos, 190, 8, 'F');
    doc.text('Classification Summary', 12, yPos + 5.5);
    doc.setTextColor(0, 0, 0);

    yPos += 15;
    doc.autoTable({
      startY: yPos,
      head: [['Category', 'Items', 'Value (SAR)', 'Percentage']],
      body: [
        ['A - High Value', data.summary.categoryA.items, data.summary.categoryA.value.toLocaleString(), `${data.summary.categoryA.percentage}%`],
        ['B - Medium Value', data.summary.categoryB.items, data.summary.categoryB.value.toLocaleString(), `${data.summary.categoryB.percentage}%`],
        ['C - Low Value', data.summary.categoryC.items, data.summary.categoryC.value.toLocaleString(), `${data.summary.categoryC.percentage}%`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [52, 144, 220], textColor: 255 },
      margin: { left: 10 },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Category A Items
    doc.setFillColor(52, 144, 220);
    doc.setTextColor(255, 255, 255);
    doc.rect(10, yPos, 190, 8, 'F');
    doc.text('Category A - High Value Items', 12, yPos + 5.5);
    doc.setTextColor(0, 0, 0);

    yPos += 15;
    doc.autoTable({
      startY: yPos,
      head: [['SKU', 'Item Name', 'Quantity', 'Unit Value', 'Total Value']],
      body: data.categoryA.map(item => [
        item.sku,
        item.name,
        item.quantity,
        `SAR ${item.unitValue.toLocaleString()}`,
        `SAR ${item.totalValue.toLocaleString()}`
      ]),
      theme: 'striped',
      headStyles: { fillColor: [239, 68, 68], textColor: 255 },
      margin: { left: 10 },
      styles: { fontSize: 8 },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
    }

    return doc.output('blob');
  }

  static async generateCycleCountReport(data) {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const qrCodeDataUrl = await QRCode.toDataURL(`CC-${data.reportId}`);
    doc.addImage(qrCodeDataUrl, 'PNG', 160, 5, 35, 35);

    doc.setFontSize(20);
    doc.setTextColor(44, 62, 80);
    doc.text('Cycle Count Report', 10, 15);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Report ID: ${data.reportId}`, 10, 22);
    doc.text(`Date: ${data.date}`, 10, 27);
    doc.text(`Assigned To: ${data.assignedTo}`, 10, 32);

    let yPos = 45;

    // Summary
    doc.setFillColor(52, 144, 220);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.rect(10, yPos, 190, 8, 'F');
    doc.text('Count Summary', 12, yPos + 5.5);
    doc.setTextColor(0, 0, 0);

    yPos += 15;
    doc.autoTable({
      startY: yPos,
      theme: 'plain',
      body: [
        ['Total Items Counted', data.summary.totalItems],
        ['Items Matched', data.summary.matched],
        ['Variances Found', data.summary.variances],
        ['Accuracy Rate', `${data.summary.accuracy}%`],
      ],
      margin: { left: 10 },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: 'bold' },
        1: { cellWidth: 60 },
      },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Variances
    if (data.variances && data.variances.length > 0) {
      doc.setFillColor(52, 144, 220);
      doc.setTextColor(255, 255, 255);
      doc.rect(10, yPos, 190, 8, 'F');
      doc.text('Variances', 12, yPos + 5.5);
      doc.setTextColor(0, 0, 0);

      yPos += 15;
      doc.autoTable({
        startY: yPos,
        head: [['SKU', 'Item Name', 'Expected', 'Counted', 'Variance', 'Location']],
        body: data.variances.map(v => [
          v.sku,
          v.name,
          v.expected,
          v.counted,
          v.variance > 0 ? `+${v.variance}` : v.variance.toString(),
          v.location
        ]),
        theme: 'striped',
        headStyles: { fillColor: [239, 68, 68], textColor: 255 },
        margin: { left: 10 },
        styles: { fontSize: 8 },
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10);
    }

    return doc.output('blob');
  }
}
